import { useAtomValue } from 'jotai'
import { useCallback } from 'react'
import type { JsonValue, ValueOf } from 'type-fest'
import * as v from 'valibot'
import { DEFAULT_LOCALE } from 'src/stores/intl'
import { localeAtom } from 'src/stores/locale'
import type { SUPPORTED_LOCALES } from './constants'
import { normalizeLng } from './utils'

type SupportedLangCode = ValueOf<typeof SUPPORTED_LOCALES>['code']

// =============================================================================
// Start Record Data variant
// =============================================================================

export type I18nRecordDataType<T = JsonValue> = Record<string, T>
/**
 * For structured data like
 * ```json
 * {
 *  "en": {
 *    "title": "Hello World",
 *    "description": "This is a description"
 *  },
 *  "ja": {
 *    "title": "こんにちは世界",
 *    "description": "これは説明です。"
 *  }
 * }
 * ```
 *
 * @param lang
 * @param data
 * @returns
 */
export function getLocalizedRecordData<T>(
  lang: string,
  data: I18nRecordDataType<T>,
) {
  const normalizedLocale = normalizeLng(lang)

  const localeData =
    // 1. Try exact match
    data[lang] ??
    // 2. Try normalized (ko->kr)
    data[normalizedLocale] ??
    // 3. Fallback to default (en)
    data[DEFAULT_LOCALE] ??
    undefined

  return localeData
}
export function useLocalizedRecordDataGetter() {
  const lang = useAtomValue(localeAtom) as SupportedLangCode
  return useCallback(
    (data: I18nRecordDataType, overrideLang?: SupportedLangCode) =>
      getLocalizedRecordData(overrideLang ?? lang, data),
    [lang],
  )
}

// =============================================================================
// End Record Data variant
// =============================================================================

// =============================================================================
// Start String Pair variant
// =============================================================================

export const I18nStringPairSchema = v.strictObject({
  lang: v.string(),
  text: v.string(),
})

export const I18nStringPairsSchema = v.array(I18nStringPairSchema)
export type I18nStringPairsType = v.InferInput<typeof I18nStringPairsSchema>

/**
 * For structured data like
 * ```json
 * [
 *  { "lang": "en", "text": "Hello World" },
 *  { "lang": "ja", "text": "こんにちは世界" }
 * ]
 * ```
 *
 * @param lang
 * @param stringPairs
 * @returns
 */
export function getLocalizedStringPairData(
  lang: string,
  stringPairs: I18nStringPairsType,
) {
  // Converting to record to reuse the utility
  const record = Object.fromEntries(
    stringPairs.map(pair => [pair.lang, pair.text]),
  )

  const resultFromRecord = getLocalizedRecordData<string>(lang, record)
  return resultFromRecord
}

export function useLocalizedStringPairGetter() {
  const lang = useAtomValue(localeAtom) as SupportedLangCode
  return useCallback(
    (stringPairs: I18nStringPairsType, overrideLang?: SupportedLangCode) =>
      getLocalizedStringPairData(overrideLang ?? lang, stringPairs),
    [lang],
  )
}

// =============================================================================
// End String Pair variant
// =============================================================================
